<template>
  <div class="m-product-gallery col-md-12 p-0">
    <div class="m-product-gallery__stage overflow-hidden col-md-8 p-0">
      <base-icon
        v-if="showCareIcon"
        icon="care-icon"
        class="m-product-gallery__care-icon a-care-icon p-3"
      />
      <badge
        v-if="isProductOnDiscount && !showAdvertisingCode"
        class="o-product-gallery__badge bg-primary"
      >
        {{ $t("ProductCard_Discount_Badge", "Tilbud") }}
      </badge>
      <badge
        v-if="isSki"
        class="o-product-card__badge bg-primary"
        :class="{'a-badge--offset': isProductOnDiscount && !showAdvertisingCode}"
      >
        {{ $t("ProductCard_Ski", "SKI") }}
      </badge>
      <badge
        v-if="showAdvertisingCode"
        class="o-product-card__badge bg-primary"
        :class="{'a-badge--offset': isSki}"
      >
        {{ $t(`ProductCard_Discount_${advertisingCode}`, advertisingCode) }}
      </badge>
      <div class="m-product-gallery__action-icons">
        <base-icon
          v-if="isProductOnLatestBoughtProductsList"
          icon="latestbought"
          class="action-icons__icon text-body"
        />
        <wishlist-button
          v-if="hasCustomerNumber"
          class="m-product-gallery__wishlist"
          :product-id="productSku"
        />
      </div>
      <vue-glide
        v-if="images.length >= 1"
        ref="glide"
        v-model="activeIndex"
        class=""
        v-bind="sliderOptions"
      >
        <vue-glide-slide
          v-for="(picture, index) in sliderContent"
          :key="'slide-' + index"
          class="glide__slide"
        >
          <base-image
            v-if="picture.url && picture.type !== 'video'"
            ref="galleryBigImage"
            class="m-product-gallery__big-image"
            image-class="js-product-image"
            :url="picture.url"
            :alt-text="picture.name"
            mode="pad"
            further-options="&bgcolor=ffffff"
            :width="imageWidth"
            :height="imageHeight"
            :ratio="imageWidth/imageHeight"
            :include-cdn="picture.includeCdn"
            @click.native="$emit('click:stage', { picture, index }), toggleModal(picture)"
          />
          <template v-else-if="picture.videoId">
            <client-only>
              <youtube
                ref="video"
                class="h-100 w-100"
                :class="{
                  '-active': hideImage
                }"
                :mute="true"
                :player-vars="options"
                :video-id="picture.videoId"
                host="https://www.youtube-nocookie.com"
                @ready="ready"
                @playing="playing"
              />
            </client-only>
          </template>
        </vue-glide-slide>
      </vue-glide>
      <vue-glide
        v-else-if="!isLoading"
        ref="glide"
        v-model="activeIndex"
        class=""
        v-bind="sliderOptions"
      >
        <vue-glide-slide
          class="glide__slide"
        >
          <base-image
            :url="placeholder.url"
            class="m-product-gallery__big-image"
            :width="imageWidth"
            :height="imageHeight"
            :alt-text="placeholder.name"
          />
        </vue-glide-slide>
      </vue-glide>
    </div>
    <div class="m-product-gallery__thumbs col-md-4 p-0 d-none d-md-block">
      <slot name="thumbs" v-bind="{ images, active: activeIndex, go }">
        <button
          v-for="(image, index) in sliderContent"
          :key="'img-' + index"
          class="m-product-gallery__item col-md-6 pl-3 pb-3"
          :class="{ '-selected': index === activeIndex }"
          @click="go(index)"
        >
          <base-image
            class="m-product-gallery__thumb"
            :url="image.url"
            :alt-text="image.name"
            :width="thumbWidth"
            mode="pad"
            further-options="&bgcolor=ffffff"
            :ratio="imageWidth/imageHeight"
            :height="thumbHeight"
            :include-cdn="image.includeCdn"
          />
        </button>
      </slot>
    </div>
    <client-only>
      <portal v-if="showModal" to="modal">
        <modal
          class="h-100"
          :visible="showModal"
          @close="toggleModal"
        >
          <div 
            v-if="clickedMedia"
            class="h-100 bg-white w-100"
          >
            <base-image
              v-if="clickedMedia.url && clickedMedia.type !== 'video'"
              :url="clickedMedia.url"
              :width="clickedMedia.width"
              :height="clickedMedia.height"
              :ratio="750/750"
              :center="clickedMedia.focalPoint"
              :alt-text="clickedMedia.name"
              :eager-load="true"
              :overlay="true"
              class="h-100 position-relative"
              image-class="h-100 w-auto mx-auto object-fit-contain"
            />
            <video
              v-else-if="clickedMedia.videoId"
              :src="cdnUrl + clickedMedia.url + '#t=0.5'"
              controls
              width="100%"
              :muted="false"
              :autoplay="true"
              :playsinline="true"
              image-class="h-100"
            />
          </div>
          <template #close>
            <base-icon icon="close" />
          </template>
        </modal>
      </portal>
    </client-only>
  </div>
</template>
<script>
import BaseImage from '@/components/atoms/BaseImage.vue'
import WishlistButton from '@/components/molecules/ShopCommerce/WishlistButton';
import BaseIcon from '@/components/atoms/BaseIcon';
import { Glide, GlideSlide } from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';
import Badge from '@/components/atoms/Badge';
import Modal from '@/components/molecules/Modal';

export default {
  name: 'ProductGallery',
  components: {
    BaseImage,
    WishlistButton,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
    BaseIcon,
    Badge,
    Modal
  },
  props: {
    product: {
      type: Object,
      default: () => {},
    },
    /**
     * Images list
     */
    images: {
      type: Array,
      default: () => [],
    },
    /**
     * Images width, without unit
     */
    imageWidth: {
      type: [Number, String],
      default: 1080,
    },
    /**
     * Images height, without unit
     */
    imageHeight: {
      type: [Number, String],
      default: 1080,
    },
    /**
     * Thumb width, without unit
     */
    thumbWidth: {
      type: [Number, String],
      default: 160,
    },
    /**
     * Thumb height, without unit
     */
    thumbHeight: {
      type: [Number, String],
      default: 160,
    },
    /**
     * Initial image number (starting from 1)
     */
    current: {
      type: Number,
      default: 1,
    },
    videos: {
      type: Array,
      default: () => [],
    },
    /**
     * Glide slider options (https://glidejs.com/docs/options/)
     */
    sliderOptions: {
      type: Object,
      default () {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0,
          perView: 1,
        };
      },
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
    productSku: {
      type: String,
      required: true,
    },
    isProductOnDiscount: {
      type: Boolean,
      default: false,
    }
  },
  data () {
    return {
      positionStatic: {},
      eventHover: {},
      glide: null,
      activeIndex: this.current - 1,
      style: '',
      hideImage: false,
      player: null,
      options: {
        modestbranding: 1,
        autoplay: 0,
        playsinline: 1,
        rel: 0,
        iv_load_policy: 3,
        controls: 0,
        autoHide: 1,
        loop: 1
      },
      showModal: false,
      clickedMedia: null,
    };
  },
  computed: {
    showCareIcon () {
      return this.product.properties.filter(x => x.name === 'Broennum_Care' && x.value === 'Ja').length > 0
    },
    sliderContent () {
      return this.images.concat(this.videos)
    },
    placeholder () {
      return this.$store.getters['site/getPlaceholder'];
    },
    hasCustomerNumber () {
      return this.$store.getters['user/user/hasCustomerNumber'];
    },
    latestBoughtProductsList () {
      return this.$store.getters['user/user/getLatestBoughtProductsList'];
    },
    isProductOnLatestBoughtProductsList () {
      return this.latestBoughtProductsList.includes(this.product.sku);
    },
    user () {
      return this.$store.getters['user/user/getUser'];
    },
    isSki () {
      return this.user?.accountProperties?.sKIAccount === 1 && this.getPropertyValue('SkiItem') === 'TRUE';
    },
    advertisingCode () {
      return this.getPropertyValue('AdvertisingCode');
    },
    showAdvertisingCode () {
      return (
        this.advertisingCode && 
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== `ProductCard_Discount_${this.advertisingCode}` && 
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== ''
      ) ? true : false;
    }
  },
  mounted () {
    if (this.hasCustomerNumber) {
      this.$store.dispatch('user/user/fetchLatestBoughtProductsList')
    }
  },
  methods: {
    go (index) {
      this.activeIndex = index;
      /**
       * Event for current image change (`v-model`)
       * @type {Event}
       */
      this.$emit('click', index + 1);
    },
    ready (event) {
      this.player = event.target;
    },
    playing () {
    //  this.setVideoSize();
      this.hideImage = true;
    },
    setVideoSize () {
      if (this.$refs.video) {
        const container = this.$refs.videoContainer;
        const iframe = this.$refs.video.$el.firstElementChild;
        iframe.style.minWidth = container.offsetWidth + 'px';
        iframe.style.minHeight = container.offsetWidth * (9 / 16) + 'px';
        iframe.style.zIndex = '0';
      }
    },
    getPropertyValue (propertyName) {
      let item = this.product.properties.find(x => x.name === propertyName && x.value !== '')
      return item?.value
    },
    toggleModal (mediaItem) {
        this.showModal = !this.showModal;
        this.clickedMedia = mediaItem;
    }
  },
};
</script>
